import { FpsOverlay } from "./fps_overlay.js";
import { MemoryProbe } from "./memory_probe.js";
import { Game } from "../game.js";
import { ScreenManager } from "../screen_manager/screen_manager.js";
import { DialogManager } from "../ui/dialog/dialog_manager.js";
import { OverlayManager } from "../ui/overlay/overlay_manager.js";
import { ImagesHolder } from "../image_loader/images_holder.js";
import { ImgID } from "../image_loader/img_id.js";
import { UIElement } from "../ui/common_ui_elements/ui_element.js";
import { CrashBreadcrumbs } from "../crash/breadcrumbs.js";
import { GraphicsSettings } from "../graphics/graphics_quality.js";
import { GIT_BRANCH } from "../generated/git_branch.js";

// Long-running jank recorder for walking through the UI by hand. Unlike
// UiPerfProbe (one screen, a few seconds) it runs for the whole session and,
// for every frame that missed vsync, keeps what was known about it: the loop
// sections, which methods ran since the previous frame (self/total time), the
// canvas and WebGL work (first-time image draws, texture uploads, shader
// compiles, canvas reallocations), the browser's long-animation-frame entry
// with its scripts, GC, input timing and what the player did just before.
// Transitions (screen, dialog, overlay) get their own settle metrics.
//
// Console: perfSession.start() / stop() / copy() / report() / mark("text")
// Debug panel: DEBUG OVERLAYS → PERF SESSION. Autostart survives reloads.

const MPX = 1e6;
const AUTOSTART_KEY = "bb_perf_session_autostart_v1";
const REPORT_KEY = "bb_perf_session_last_v1";

const MAX_DETAILED_HITCHES = 250;
const MAX_TIMELINE = 4000;
const MAX_FRAMES = 250_000;
const CONTEXT_FRAMES = 6;
const RECENT_EVENTS_MS = 1500;
const SLOW_OP_MS = 1.5;
const SPAN_MIN_MS = 0.3;
const ASYNC_LOG_MS = 60;
const TRANSITION_WINDOW_MS = 1500;
const HOT_CALLS_PER_S = 1500;
const HOT_AVG_MS = 0.02;
const SHORT_HITCHES = 50;
const SHORT_TIMELINE = 400;

type Fn = ( ...args: unknown[] ) => unknown;
type Proto = Record<string, unknown>;

type SpanAgg = { calls: number; total: number; self: number; max: number };
type SessionSpan = SpanAgg & { hitch_self: number; hitch_calls: number };
type SlowOp = { kind: string; label: string; ms: number };

type Counters = {
  ops: number; img: number; img_mpx: number; text: number; stroke: number; shadow: number; filter: number;
  off_ops: number; new_src: number; new_src_mpx: number; draw_ms: number;
  tex: number; tex_mpx: number; tex_ms: number; shader: number; shader_ms: number; readback: number; readback_ms: number;
  gl_draws: number; resize: number; resize_ms: number;
};

type FrameRow = { t: number; dt: number; js: number; phys: number; game: number; ui: number; gl: number };

type Loaf = {
  start: number; end: number; ms: number; render_ms: number; layout_ms: number; blocking_ms: number;
  scripts: { invoker: string; fn: string; src: string; ms: number; layout_ms: number }[];
};

type InputTiming = { t: number; name: string; delay: number; proc: number; total: number };

type Hitch = {
  t: number; dt: number; js: number; phys: number; game: number; ui: number; gl: number;
  gap_js: number;
  probe: number;
  screen: string; dialog: string; overlay: string;
  prev: number[];
  next: number[];
  spans: { key: string; total: number; self: number; calls: number }[];
  c: Counters;
  slow: SlowOp[];
  heap: number; gc: number;
  events: string[];
  cause: string;
  loaf: string;
  input: string;
  mem: string;
};

type Transition = {
  t: number; kind: string; name: string; trigger: string;
  frames: number; first_dt: number; max_dt: number; hitches: number; jank_ms: number; settle_ms: number;
  closed: boolean; quiet_run: number;
};

type TimelineEvent = { t: number; kind: string; text: string; base: string; repeat?: number };

type Wrapped = { owner: Proto; name: string; original: Fn; key: string; calls_window: number; ms_window: number };

export class PerfSession
{
  private static recording = false;
  /** Data of the last stopped session is still in memory, so a short report can be rebuilt. */
  private static has_data = false;
  private static started_at = 0;
  private static started_wall = "";
  private static vsync = 16.67;
  private static vsync_samples: number[] = [];

  // frames
  private static prev_frame_start = 0;
  private static frame_start = 0;
  private static frame_count = 0;
  private static screen_dts = new Map<string, number[]>();
  private static ring: FrameRow[] = [];
  private static pending_next: { hitch: Hitch; left: number }[] = [];
  private static hidden_since = 0;
  private static skip_next = false;

  // per-frame accumulators
  private static cur: Counters = PerfSession.emptyCounters();
  private static frame_spans = new Map<string, SpanAgg>();
  private static slow_ops: SlowOp[] = [];
  private static loop_js = -1;
  private static self_at_loop_end = 0;
  private static probe_ms = 0;

  // spans
  private static wrapped: Wrapped[] = [];
  private static wrapped_protos = new WeakSet<object>();
  private static span_stack: { key: string; t0: number; child: number }[] = [];
  private static session_spans = new Map<string, SessionSpan>();
  private static hot_skipped: string[] = [];
  private static async_stats = new Map<string, { n: number; total: number; max: number; rejected: number }>();
  private static last_tree_walk = 0;
  private static last_hot_check = 0;

  // canvas / gl hooks
  private static hook_originals: { owner: Proto; name: string; fn?: Fn; desc?: PropertyDescriptor }[] = [];
  private static seen_sources = new WeakSet<object>();
  private static image_labels = new WeakMap<object, string>();
  private static labels_built_at = 0;

  // hitches, transitions, timeline
  private static hitches: Hitch[] = [];
  private static hitch_total = 0;
  private static hitch_by_cause = new Map<string, { n: number; ms: number }>();
  private static transitions: Transition[] = [];
  private static timeline: TimelineEvent[] = [];
  private static loafs: Loaf[] = [];
  private static inputs: InputTiming[] = [];
  private static observers: PerformanceObserver[] = [];
  private static loaf_supported = false;
  private static event_timing_supported = false;
  private static resources: { t: number; name: string; ms: number; kb: number }[] = [];
  private static prev_heap = 0;
  private static last_tap: { t: number; text: string } | null = null;
  private static dom_listeners: { target: EventTarget; type: string; fn: EventListener; capture: boolean }[] = [];
  private static dialog_names = "";
  private static overlay_names = "";
  private static last_mem_note = 0;
  private static marks = 0;

  // UI
  private static hud: HTMLDivElement | null = null;
  private static hud_text: HTMLSpanElement | null = null;
  private static panel: HTMLDivElement | null = null;
  private static last_hud_update = 0;
  private static last_report = "";
  private static worst_dt = 0;

  // ---- public API ----

  public static isRecording(): boolean { return this.recording; }

  public static isAutostart(): boolean
  {
    try { return localStorage.getItem( AUTOSTART_KEY ) === "1"; }
    catch { return false; }
  }

  public static setAutostart( on: boolean ): void
  {
    try
    {
      if ( on ) localStorage.setItem( AUTOSTART_KEY, "1" );
      else localStorage.removeItem( AUTOSTART_KEY );
    }
    catch {}
  }

  /** Called once from bootstrap: resumes recording on every launch while autostart is on. */
  public static maybeAutostart(): void
  {
    if ( this.isAutostart() ) this.start();
  }

  public static toggle(): void
  {
    if ( this.recording ) this.stop();
    else this.start();
  }

  public static start(): void
  {
    if ( this.recording ) return;
    this.recording = true;
    this.started_at = performance.now();
    this.started_wall = new Date().toISOString();
    this.vsync = 16.67;
    this.vsync_samples = [];
    this.prev_frame_start = 0;
    this.frame_start = 0;
    this.frame_count = 0;
    this.screen_dts.clear();
    this.ring = [];
    this.pending_next = [];
    this.cur = PerfSession.emptyCounters();
    this.frame_spans.clear();
    this.slow_ops = [];
    this.loop_js = -1;
    this.self_at_loop_end = 0;
    this.probe_ms = 0;
    this.span_stack = [];
    this.session_spans.clear();
    this.hot_skipped = [];
    this.async_stats.clear();
    this.hitches = [];
    this.hitch_total = 0;
    this.hitch_by_cause.clear();
    this.transitions = [];
    this.timeline = [];
    this.loafs = [];
    this.inputs = [];
    this.resources = [];
    this.prev_heap = 0;
    this.last_tap = null;
    this.seen_sources = new WeakSet();
    this.worst_dt = 0;
    this.marks = 0;
    this.dialog_names = "";
    this.overlay_names = "";

    FpsOverlay.session_marks = true;
    CrashBreadcrumbs.listener = ( type, data ) => PerfSession.onBreadcrumb( type, data );
    this.installCanvasHooks();
    this.installGlHooks();
    this.instrumentManagers();
    this.walkTrees();
    this.observe();
    this.listenDom();
    this.showHud();
    this.event( "session", `start screen=${this.screenName()}` );
    console.log( "[PERFSESSION] recording started" );
  }

  public static stop(): string
  {
    if ( !this.recording ) return this.last_report || this.loadSaved();
    this.event( "session", "stop" );
    const report = this.buildReport( true );
    const short = this.buildReport( false );
    this.teardown();
    this.publish( report );
    this.showPanel( short, report.length );
    return report;
  }

  private static buildOrLast( full: boolean ): string
  {
    if ( !this.recording && !this.has_data ) return this.report();
    const text = this.buildReport( full );
    if ( full ) this.publish( text );
    return text;
  }

  /** Builds the report so far without stopping. */
  public static snapshot(): string
  {
    if ( !this.recording ) return this.report();
    const report = this.buildReport();
    this.publish( report );
    return report;
  }

  public static report(): string { return this.last_report || this.loadSaved(); }

  /** Manual marker from the HUD ("it lagged here") or the console. */
  public static mark( text = "" ): void
  {
    if ( !this.recording ) return;
    this.marks++;
    this.event( "MARK", `#${this.marks} ${text}`.trim() );
    this.flashHud( `mark #${this.marks}` );
  }

  /**
   * Saves the full report as perf_session_<date>.txt: on device through the
   * same native share sheet as the log overlay's SAVE, in a browser as a download.
   */
  public static async save( full = true ): Promise<boolean>
  {
    const text = this.recording || !full ? this.buildOrLast( full ) : this.report();
    if ( !text ) return false;
    const cap = ( window as unknown as { Capacitor?: { isNativePlatform?: () => boolean; Plugins?: Record<string, { shareLogs?: ( o: { text: string; prefix: string } ) => Promise<void> }> } } ).Capacitor;
    if ( cap?.isNativePlatform?.() )
    {
      const share = cap.Plugins?.UtilityPlugin?.shareLogs;
      if ( !share ) return false;
      try { await share( { text, prefix: "perf_session" } ); return true; }
      catch ( e ) { console.warn( "[PERFSESSION] share failed", e ); return false; }
    }
    const d = new Date();
    const two = ( n: number ) => String( n ).padStart( 2, "0" );
    const ts = `${d.getFullYear()}${two( d.getMonth() + 1 )}${two( d.getDate() )}_${two( d.getHours() )}${two( d.getMinutes() )}${two( d.getSeconds() )}`;
    const url = URL.createObjectURL( new Blob( [ text ], { type: "text/plain;charset=utf-8" } ) );
    const a = document.createElement( "a" );
    a.href = url;
    a.download = `perf_session_${ts}${full ? "" : "_short"}.txt`;
    a.click();
    setTimeout( () => URL.revokeObjectURL( url ), 1000 );
    return true;
  }

  /** Short = summaries, the 50 worst hitches and a trimmed timeline; small enough to paste into a chat. */
  public static async copy( full = false ): Promise<boolean>
  {
    const text = this.recording || !full ? this.buildOrLast( full ) : this.report();
    if ( !text ) return false;
    try
    {
      await navigator.clipboard.writeText( text );
      return true;
    }
    catch {}
    const area = document.createElement( "textarea" );
    area.value = text;
    area.style.cssText = "position:fixed;top:0;left:0;opacity:0;";
    document.body.appendChild( area );
    area.select();
    let ok = false;
    try { ok = document.execCommand( "copy" ); }
    catch {}
    area.remove();
    return ok;
  }

  // ---- loop hooks ----

  // A frame interval runs from one loop start to the next. It holds the
  // previous loop's work plus whatever ran after it (timers, input handlers,
  // promise callbacks, raster/GC), so a slow loop shows up as the long dt of
  // the interval it opened — that is the interval evaluated here.
  public static onFrameStart( now: number ): void
  {
    if ( !this.recording ) return;

    const dt = this.prev_frame_start > 0 ? now - this.prev_frame_start : 0;
    const had_loop = this.loop_js >= 0;
    const between = Math.max( 0, this.sumSelf( this.frame_spans ) - this.self_at_loop_end );

    const heap = PerfSession.heapMb();
    const gc = this.prev_heap > 0 && heap < this.prev_heap ? this.prev_heap - heap : 0;
    this.prev_heap = heap;

    const skip = this.skip_next || !had_loop || dt <= 0 || dt > 5000;
    this.skip_next = false;

    if ( !skip && this.frame_count < MAX_FRAMES )
    {
      this.frame_count++;
      if ( this.vsync_samples.length < 240 )
      {
        this.vsync_samples.push( dt );
        if ( this.vsync_samples.length % 30 === 0 )
        {
          // Median: on a 120 Hz phone the low quartile landed on rAF jitter
          // (7.4 ms), which turned every on-time 8.3 ms frame into "jank".
          const sorted = [ ...this.vsync_samples ].sort( ( a, b ) => a - b );
          this.vsync = Math.max( 4, sorted[Math.floor( sorted.length * 0.5 )] );
        }
      }

      const screen = this.screenName();
      let list = this.screen_dts.get( screen );
      if ( !list ) { list = []; this.screen_dts.set( screen, list ); }
      list.push( dt );

      const s = FpsOverlay.lastSections();
      const row: FrameRow = { t: now - this.started_at, dt, js: this.loop_js, phys: s.phys, game: s.game, ui: s.ui2d, gl: s.gl };

      for ( let i = this.pending_next.length - 1; i >= 0; i-- )
      {
        const p = this.pending_next[i];
        p.hitch.next.push( Math.round( dt ) );
        if ( --p.left <= 0 ) this.pending_next.splice( i, 1 );
      }

      this.feedTransitions( now, dt );

      if ( dt >= this.hitchThreshold() )
        this.recordHitch( row, between, heap, gc );

      this.ring.push( row );
      if ( this.ring.length > CONTEXT_FRAMES ) this.ring.shift();
      if ( dt > this.worst_dt ) this.worst_dt = dt;
    }

    // Session totals of every span, then reset the per-interval buckets.
    for ( const [ key, agg ] of this.frame_spans )
    {
      let t = this.session_spans.get( key );
      if ( !t ) { t = { calls: 0, total: 0, self: 0, max: 0, hitch_self: 0, hitch_calls: 0 }; this.session_spans.set( key, t ); }
      t.calls += agg.calls;
      t.total += agg.total;
      t.self += agg.self;
      if ( agg.max > t.max ) t.max = agg.max;
    }
    this.frame_spans.clear();
    this.cur = PerfSession.emptyCounters();
    this.slow_ops = [];
    this.self_at_loop_end = 0;
    this.probe_ms = 0;
    this.loop_js = -1;

    this.prev_frame_start = now;
    this.frame_start = now;
  }

  public static onFrameEnd( now: number ): void
  {
    if ( !this.recording ) return;
    this.loop_js = now - this.frame_start;
    this.self_at_loop_end = this.sumSelf( this.frame_spans );

    // Housekeeping lands between frames; its cost is reported, not blamed on the game.
    if ( now - this.last_tree_walk > 1000 )
    {
      this.last_tree_walk = now;
      this.walkTrees();
      this.refreshStackNames();
    }
    if ( now - this.last_hot_check > 1000 )
    {
      this.dropHotWrappers( now - this.last_hot_check );
      this.last_hot_check = now;
    }
    if ( now - this.last_mem_note > 10_000 )
    {
      this.last_mem_note = now;
      const mem = this.memText();
      if ( mem ) this.event( "mem", mem );
    }
    if ( now - this.last_hud_update > 500 ) { this.last_hud_update = now; this.updateHud(); }
    this.probe_ms += performance.now() - now;
  }

  private static hitchThreshold(): number
  {
    // One whole dropped frame (plus slack for rAF jitter), never under 20 ms.
    return Math.max( 20, this.vsync * 1.7 );
  }

  // ---- hitches ----

  private static recordHitch( row: FrameRow, between: number, heap: number, gc: number ): void
  {
    this.hitch_total++;
    const spans = [ ...this.frame_spans.entries() ]
      .map( ( [ key, a ] ) => ( { key, total: a.total, self: a.self, calls: a.calls } ) )
      .filter( s => s.total >= SPAN_MIN_MS )
      .sort( ( a, b ) => b.self - a.self )
      .slice( 0, 14 );

    for ( const [ key, agg ] of this.frame_spans )
    {
      const s = this.session_spans.get( key );
      if ( s ) { s.hitch_self += agg.self; s.hitch_calls += agg.calls; }
      else this.session_spans.set( key, { calls: 0, total: 0, self: 0, max: 0, hitch_self: agg.self, hitch_calls: agg.calls } );
    }

    const hitch: Hitch = {
      t: row.t, dt: row.dt, js: row.js, phys: row.phys, game: row.game, ui: row.ui, gl: row.gl,
      gap_js: between,
      probe: this.probe_ms,
      screen: this.screenName(),
      dialog: this.dialog_names,
      overlay: this.overlay_names,
      prev: this.ring.map( r => Math.round( r.dt ) ),
      next: [],
      spans,
      c: { ...this.cur },
      slow: [ ...this.slow_ops ].sort( ( a, b ) => b.ms - a.ms ).slice( 0, 10 ),
      heap, gc,
      events: this.recentEvents( row.t ),
      cause: "",
      loaf: "",
      input: "",
      mem: "",
    };
    hitch.cause = this.guessCause( hitch );
    const by = this.hitch_by_cause.get( hitch.cause ) ?? { n: 0, ms: 0 };
    by.n++;
    by.ms += row.dt - this.vsync;
    this.hitch_by_cause.set( hitch.cause, by );

    this.pending_next.push( { hitch, left: 3 } );

    if ( this.hitches.length < MAX_DETAILED_HITCHES ) this.hitches.push( hitch );
    else
    {
      // Keep the worst ones once the cap is hit.
      let min_i = 0;
      for ( let i = 1; i < this.hitches.length; i++ )
        if ( this.hitches[i].dt < this.hitches[min_i].dt ) min_i = i;
      if ( this.hitches[min_i].dt < hitch.dt ) this.hitches[min_i] = hitch;
    }
  }

  private static guessCause( h: Hitch ): string
  {
    const loop_js = h.js;
    const between = h.gap_js;
    const out = h.dt - loop_js - between - h.probe;
    const parts: { name: string; ms: number }[] = [
      { name: "js:phys", ms: h.phys },
      { name: "js:game(update)", ms: h.game },
      { name: "js:ui2d(render)", ms: h.ui },
      { name: "js:gl(three)", ms: h.gl },
      { name: "js:between-frames", ms: between },
    ];
    const top = parts.sort( ( a, b ) => b.ms - a.ms )[0];
    const tags: string[] = [];
    if ( h.c.shader > 0 ) tags.push( "shader-compile" );
    if ( h.c.tex_mpx > 0.5 || h.c.tex_ms > 3 ) tags.push( "texture-upload" );
    if ( h.c.new_src > 0 ) tags.push( "first-image-draw" );
    if ( h.c.resize > 0 ) tags.push( "canvas-realloc" );
    if ( h.c.readback > 0 ) tags.push( "gpu-readback" );
    if ( h.gc > 1 ) tags.push( "gc" );
    const main = top.ms >= Math.max( 8, out ) ? top.name : "outside-js(raster/gpu/compositor/other)";
    return tags.length ? `${main} +${tags.join( "+" )}` : main;
  }

  // ---- transitions ----

  private static beginTransition( kind: string, name: string ): void
  {
    const now = performance.now();
    const tap = this.last_tap && now - this.last_tap.t < 700 ? `tap ${Math.round( now - this.last_tap.t )}ms before: ${this.last_tap.text}` : "programmatic";
    this.transitions.push( {
      t: now - this.started_at, kind, name, trigger: tap,
      frames: 0, first_dt: 0, max_dt: 0, hitches: 0, jank_ms: 0, settle_ms: 0, closed: false, quiet_run: 0,
    } );
    if ( this.transitions.length > 600 ) this.transitions.shift();
  }

  private static feedTransitions( frame_start: number, dt: number ): void
  {
    const t = frame_start - this.started_at;
    const threshold = this.hitchThreshold();
    for ( const tr of this.transitions )
    {
      if ( tr.closed ) continue;
      const age = t - tr.t;
      if ( age < 0 ) continue;
      if ( age > TRANSITION_WINDOW_MS )
      {
        tr.closed = true;
        if ( tr.settle_ms === 0 ) tr.settle_ms = -1;
        continue;
      }
      tr.frames++;
      if ( tr.frames === 1 ) tr.first_dt = dt;
      if ( dt > tr.max_dt ) tr.max_dt = dt;
      if ( dt >= threshold ) { tr.hitches++; tr.quiet_run = 0; }
      else tr.quiet_run++;
      tr.jank_ms += Math.max( 0, dt - this.vsync );
      // Settled = 10 frames in a row on vsync; time is measured to the first of them.
      if ( tr.settle_ms === 0 && tr.quiet_run >= 10 )
        tr.settle_ms = Math.max( 0, age - 9 * this.vsync );
    }
  }

  // ---- timeline ----

  private static event( kind: string, text: string ): void
  {
    if ( !this.recording ) return;
    const t = performance.now() - this.started_at;
    const last = this.timeline[this.timeline.length - 1];
    if ( last && last.kind === kind && last.base === text && t - last.t < 2000 )
    {
      last.repeat = ( last.repeat ?? 1 ) + 1;
      last.text = `${text} (x${last.repeat})`;
      return;
    }
    this.timeline.push( { t, kind, text, base: text } );
    if ( this.timeline.length > MAX_TIMELINE ) this.timeline.splice( 0, this.timeline.length - MAX_TIMELINE );
  }

  private static recentEvents( t: number ): string[]
  {
    const out: string[] = [];
    for ( let i = this.timeline.length - 1; i >= 0; i-- )
    {
      const e = this.timeline[i];
      if ( t - e.t > RECENT_EVENTS_MS ) break;
      if ( e.kind === "mem" ) continue;
      out.push( `${( e.t - t ).toFixed( 0 )}ms ${e.kind}: ${e.text}` );
      if ( out.length >= 8 ) break;
    }
    return out.reverse();
  }

  /** Breadcrumb funnel: screens, dialogs, http, ws, level, console and app events. */
  public static onBreadcrumb( type: string, data: string ): void
  {
    if ( !this.recording ) return;
    this.event( type, data );
    if ( type === "dialog" )
    {
      const [ verb, ...rest ] = data.split( " " );
      this.beginTransition( `dialog-${verb}`, rest.join( " " ) );
      this.refreshStackNames();
    }
    else if ( type === "screen" )
    {
      const req = this.screen_request;
      const since = req && performance.now() - req.t < 10_000 ? ` (showScreen(${req.name}) called ${( performance.now() - req.t ).toFixed( 0 )}ms ago)` : "";
      this.screen_request = null;
      const last = this.timeline[this.timeline.length - 1];
      if ( last ) { last.text += since; last.base += since; }
      this.beginTransition( "screen", data );
      // New screen roots get their methods wrapped on the next frame, not a second later.
      this.last_tree_walk = 0;
    }
  }

  private static screen_request: { t: number; name: string } | null = null;

  /** Transitions that have no breadcrumb of their own, keyed by wrapped method. */
  private static readonly CALL_HOOKS: Record<string, ( args: unknown[] ) => void> = {
    "ScreenManager.showScreen": args =>
    {
      PerfSession.screen_request = { t: performance.now(), name: String( args[0] ) };
      PerfSession.event( "nav", `showScreen(${String( args[0] )}) requested from ${PerfSession.contextText()}` );
    },
    "OverlayManager.openOverlay": args =>
    {
      const name = args[0] && typeof args[0] === "object" ? PerfSession.nameOf( args[0] as object ) : "?";
      PerfSession.event( "overlay", `open ${name}` );
      PerfSession.beginTransition( "overlay-open", name );
    },
    "OverlayManager.closeOverlay": () =>
    {
      const top = PerfSession.overlayStack().pop();
      const name = top ? PerfSession.nameOf( top ) : "?";
      PerfSession.event( "overlay", `close ${name}` );
      PerfSession.beginTransition( "overlay-close", name );
    },
  };

  // ---- span instrumentation ----

  private static instrumentManagers(): void
  {
    const statics: [ string, object ][] = [
      [ "ScreenManager", ScreenManager ],
      [ "DialogManager", DialogManager ],
      [ "OverlayManager", OverlayManager ],
      [ "ImagesHolder", ImagesHolder ],
    ];
    for ( const [ name, cls ] of statics ) this.wrapOwner( cls as unknown as Proto, name, true );
  }

  private static walkTrees(): void
  {
    const roots: unknown[] = [];
    // Game is created after an autostarted session begins, so it is picked up here.
    if ( Game.instance ) roots.push( Game.instance );
    const screen_ui = Game.instance?.currentScreenUi( ScreenManager.getCurrentScreen() );
    if ( screen_ui ) roots.push( screen_ui );
    const screen_instance = ScreenManager.getCurrentScreenInstance();
    if ( screen_instance ) roots.push( screen_instance );
    for ( const d of this.dialogStack() ) roots.push( d );
    for ( const o of this.overlayStack() ) roots.push( o );

    const seen = new Set<object>();
    const visit = ( el: unknown, depth: number ): void =>
    {
      if ( !el || typeof el !== "object" || seen.has( el ) || depth > 40 ) return;
      seen.add( el );
      this.wrapProtoChain( el );
      if ( el instanceof UIElement )
        for ( const child of el.getChildren() ) visit( child, depth + 1 );
    };
    for ( const root of roots ) visit( root, 0 );
  }

  private static wrapProtoChain( obj: object ): void
  {
    let proto = Object.getPrototypeOf( obj ) as Proto | null;
    while ( proto && proto !== Object.prototype )
    {
      if ( !this.wrapped_protos.has( proto ) )
      {
        this.wrapped_protos.add( proto );
        const ctor = ( proto as { constructor?: { name?: string } } ).constructor;
        this.wrapOwner( proto, ( ctor?.name || "?" ).replace( /^_+/, "" ), false );
      }
      proto = Object.getPrototypeOf( proto ) as Proto | null;
    }
  }

  private static wrapOwner( owner: Proto, owner_name: string, is_static: boolean ): void
  {
    for ( const name of Object.getOwnPropertyNames( owner ) )
    {
      if ( name === "constructor" || name === "prototype" || name === "length" || name === "name" ) continue;
      const desc = Object.getOwnPropertyDescriptor( owner, name );
      if ( !desc || typeof desc.value !== "function" || !desc.writable ) continue;
      const original = desc.value as Fn & { __perf_wrapped?: boolean };
      if ( original.__perf_wrapped ) continue;
      let src = "";
      try { src = Function.prototype.toString.call( original ); }
      catch {}
      if ( src.startsWith( "class" ) ) continue;
      if ( is_static && /^[A-Z_0-9]+$/.test( name ) ) continue;
      // Accessor-style methods are called thousands of times a frame and cost nothing.
      if ( /^(get|is|has|should|can)[A-Z_]/.test( name ) || name === "name" ) continue;

      const key = `${owner_name}${is_static ? "." : "#"}${name}`;
      const entry: Wrapped = { owner, name, original, key, calls_window: 0, ms_window: 0 };
      const wrapper = function ( this: unknown, ...args: unknown[] ): unknown
      {
        if ( !PerfSession.recording ) return original.apply( this, args );
        return PerfSession.runSpan( entry, this, args );
      };
      ( wrapper as unknown as { __perf_wrapped: boolean } ).__perf_wrapped = true;
      try
      {
        owner[name] = wrapper;
        this.wrapped.push( entry );
      }
      catch {}
    }
  }

  private static runSpan( entry: Wrapped, self: unknown, args: unknown[] ): unknown
  {
    const hook = PerfSession.CALL_HOOKS[entry.key];
    if ( hook )
    {
      try { hook( args ); }
      catch {}
    }
    const t0 = performance.now();
    const top_level = this.span_stack.length === 0;
    const frame = { key: entry.key, t0, child: 0 };
    this.span_stack.push( frame );
    let result: unknown;
    try
    {
      result = entry.original.apply( self, args );
    }
    finally
    {
      const t1 = performance.now();
      this.span_stack.pop();
      const total = t1 - t0;
      const own = total - frame.child;
      const parent = this.span_stack[this.span_stack.length - 1];
      if ( parent ) parent.child += total;
      entry.calls_window++;
      entry.ms_window += total;
      let agg = this.frame_spans.get( entry.key );
      if ( !agg ) { agg = { calls: 0, total: 0, self: 0, max: 0 }; this.frame_spans.set( entry.key, agg ); }
      agg.calls++;
      agg.self += own;
      // Recursion (render → children render) would count a subtree twice in total.
      if ( !this.span_stack.some( s => s.key === entry.key ) ) agg.total += total;
      if ( total > agg.max ) agg.max = total;
    }
    // Note: this attaches a rejection handler, so while recording a rejected
    // async method no longer reaches unhandledrejection.
    if ( result && typeof ( result as Promise<unknown> ).then === "function" )
    {
      ( result as Promise<unknown> ).then(
        () => this.asyncDone( entry.key, t0, "ok", top_level ),
        () => this.asyncDone( entry.key, t0, "rejected", top_level )
      );
    }
    return result;
  }

  private static asyncDone( key: string, t0: number, how: string, top_level: boolean ): void
  {
    if ( !this.recording ) return;
    const ms = performance.now() - t0;
    const agg = this.async_stats.get( key ) ?? { n: 0, total: 0, max: 0, rejected: 0 };
    agg.n++;
    agg.total += ms;
    if ( ms > agg.max ) agg.max = ms;
    if ( how !== "ok" ) agg.rejected++;
    this.async_stats.set( key, agg );
    // Nested async calls (per-image loads under prepareScreenImages) only go to the table.
    if ( top_level && ms >= ASYNC_LOG_MS ) this.event( "async", `${key} ${how} after ${ms.toFixed( 0 )}ms (wall, awaits included)` );
  }

  private static sumSelf( map: Map<string, SpanAgg> ): number
  {
    let sum = 0;
    for ( const a of map.values() ) sum += a.self;
    return sum;
  }

  /** Cheap methods called thousands of times a second cost more to time than they take. */
  private static dropHotWrappers( window_ms: number ): void
  {
    const per_s = 1000 / Math.max( 1, window_ms );
    for ( let i = this.wrapped.length - 1; i >= 0; i-- )
    {
      const w = this.wrapped[i];
      const calls = w.calls_window * per_s;
      if ( calls > HOT_CALLS_PER_S && w.ms_window / Math.max( 1, w.calls_window ) < HOT_AVG_MS )
      {
        this.hot_skipped.push( `${w.key} (${calls.toFixed( 0 )}/s)` );
        if ( w.owner[w.name] && ( w.owner[w.name] as { __perf_wrapped?: boolean } ).__perf_wrapped )
          w.owner[w.name] = w.original;
        ( w.original as unknown as { __perf_wrapped?: boolean } ).__perf_wrapped = true; // never re-wrap
        this.wrapped.splice( i, 1 );
        continue;
      }
      w.calls_window = 0;
      w.ms_window = 0;
    }
  }

  private static unwrapAll(): void
  {
    for ( const w of this.wrapped )
      if ( ( w.owner[w.name] as { __perf_wrapped?: boolean } | undefined )?.__perf_wrapped ) w.owner[w.name] = w.original;
    // Hot-skipped originals were tagged so they are not re-wrapped this session; clear for the next one.
    this.wrapped = [];
    this.wrapped_protos = new WeakSet();
  }

  // ---- canvas / webgl hooks ----

  private static emptyCounters(): Counters
  {
    return {
      ops: 0, img: 0, img_mpx: 0, text: 0, stroke: 0, shadow: 0, filter: 0, off_ops: 0, new_src: 0, new_src_mpx: 0, draw_ms: 0,
      tex: 0, tex_mpx: 0, tex_ms: 0, shader: 0, shader_ms: 0, readback: 0, readback_ms: 0, gl_draws: 0, resize: 0, resize_ms: 0,
    };
  }

  private static hookMethod( owner: Proto, name: string, make: ( original: Fn ) => Fn ): void
  {
    const original = owner[name];
    if ( typeof original !== "function" ) return;
    this.hook_originals.push( { owner, name, fn: original as Fn } );
    owner[name] = make( original as Fn );
  }

  private static installCanvasHooks(): void
  {
    const protos: Proto[] = [ CanvasRenderingContext2D.prototype as unknown as Proto ];
    if ( typeof OffscreenCanvasRenderingContext2D !== "undefined" )
      protos.push( OffscreenCanvasRenderingContext2D.prototype as unknown as Proto );

    for ( const proto of protos )
    {
      this.hookMethod( proto, "drawImage", original => function ( this: CanvasRenderingContext2D, ...args: unknown[] )
      {
        if ( !PerfSession.recording ) return original.apply( this, args );
        const t0 = performance.now();
        const out = original.apply( this, args );
        PerfSession.onDrawImage( this, args, performance.now() - t0 );
        return out;
      } );
      for ( const name of [ "fillText", "strokeText" ] )
        this.hookMethod( proto, name, original => function ( this: CanvasRenderingContext2D, ...args: unknown[] )
        {
          if ( PerfSession.recording ) PerfSession.onText( this, name === "strokeText" );
          return original.apply( this, args );
        } );
      for ( const name of [ "fill", "stroke", "fillRect" ] )
        this.hookMethod( proto, name, original => function ( this: CanvasRenderingContext2D, ...args: unknown[] )
        {
          if ( PerfSession.recording ) PerfSession.onShape( this );
          return original.apply( this, args );
        } );
      for ( const name of [ "getImageData", "putImageData" ] )
        this.hookMethod( proto, name, original => function ( this: CanvasRenderingContext2D, ...args: unknown[] )
        {
          if ( !PerfSession.recording ) return original.apply( this, args );
          const t0 = performance.now();
          const out = original.apply( this, args );
          const ms = performance.now() - t0;
          PerfSession.cur.readback++;
          PerfSession.cur.readback_ms += ms;
          if ( ms >= SLOW_OP_MS ) PerfSession.slow( `2d.${name}`, PerfSession.canvasLabel( this.canvas ), ms );
          return out;
        } );
    }

    // Resizing a canvas reallocates its backing store (and its GPU texture).
    const canvas_proto = HTMLCanvasElement.prototype as unknown as Proto;
    for ( const prop of [ "width", "height" ] )
    {
      const desc = Object.getOwnPropertyDescriptor( canvas_proto, prop );
      if ( !desc?.set || !desc.get ) continue;
      this.hook_originals.push( { owner: canvas_proto, name: prop, desc } );
      const set = desc.set;
      Object.defineProperty( canvas_proto, prop, {
        configurable: true,
        enumerable: desc.enumerable,
        get: desc.get,
        set( this: HTMLCanvasElement, v: number )
        {
          if ( !PerfSession.recording ) { set.call( this, v ); return; }
          const before = ( desc.get as () => number ).call( this );
          const t0 = performance.now();
          set.call( this, v );
          const ms = performance.now() - t0;
          if ( before === v ) return;
          PerfSession.cur.resize++;
          PerfSession.cur.resize_ms += ms;
          const label = `#${this.id || "?"} ${prop} ${before}->${v}`;
          if ( ms >= SLOW_OP_MS || v * ( prop === "width" ? this.height : this.width ) > 1e6 )
            PerfSession.slow( "canvas.resize", label, ms );
        },
      } );
    }
  }

  private static installGlHooks(): void
  {
    const protos: Proto[] = [];
    if ( typeof WebGL2RenderingContext !== "undefined" ) protos.push( WebGL2RenderingContext.prototype as unknown as Proto );
    if ( typeof WebGLRenderingContext !== "undefined" ) protos.push( WebGLRenderingContext.prototype as unknown as Proto );

    for ( const proto of protos )
    {
      for ( const name of [ "texImage2D", "texSubImage2D", "texImage3D", "texStorage2D", "compressedTexImage2D" ] )
        this.hookMethod( proto, name, original => function ( this: WebGLRenderingContext, ...args: unknown[] )
        {
          if ( !PerfSession.recording ) return original.apply( this, args );
          const t0 = performance.now();
          const out = original.apply( this, args );
          const ms = performance.now() - t0;
          const px = PerfSession.texPixels( args );
          PerfSession.cur.tex++;
          PerfSession.cur.tex_mpx += px / MPX;
          PerfSession.cur.tex_ms += ms;
          if ( ms >= SLOW_OP_MS || px > 1e6 ) PerfSession.slow( `gl.${name}`, PerfSession.texLabel( args, px ), ms );
          return out;
        } );
      for ( const name of [ "compileShader", "linkProgram", "getProgramParameter", "getShaderParameter", "getProgramInfoLog" ] )
        this.hookMethod( proto, name, original => function ( this: WebGLRenderingContext, ...args: unknown[] )
        {
          if ( !PerfSession.recording ) return original.apply( this, args );
          const t0 = performance.now();
          const out = original.apply( this, args );
          const ms = performance.now() - t0;
          if ( name === "compileShader" || name === "linkProgram" ) PerfSession.cur.shader++;
          PerfSession.cur.shader_ms += ms;
          // Parameter queries block until the driver finished compiling — that is where the time lands.
          if ( ms >= SLOW_OP_MS ) PerfSession.slow( `gl.${name}`, "", ms );
          return out;
        } );
      for ( const name of [ "readPixels", "finish" ] )
        this.hookMethod( proto, name, original => function ( this: WebGLRenderingContext, ...args: unknown[] )
        {
          if ( !PerfSession.recording ) return original.apply( this, args );
          const t0 = performance.now();
          const out = original.apply( this, args );
          const ms = performance.now() - t0;
          PerfSession.cur.readback++;
          PerfSession.cur.readback_ms += ms;
          if ( ms >= SLOW_OP_MS ) PerfSession.slow( `gl.${name}`, "", ms );
          return out;
        } );
      for ( const name of [ "drawArrays", "drawElements", "drawArraysInstanced", "drawElementsInstanced" ] )
        this.hookMethod( proto, name, original => function ( this: WebGLRenderingContext, ...args: unknown[] )
        {
          if ( PerfSession.recording ) PerfSession.cur.gl_draws++;
          return original.apply( this, args );
        } );
    }
  }

  private static removeHooks(): void
  {
    for ( let i = this.hook_originals.length - 1; i >= 0; i-- )
    {
      const h = this.hook_originals[i];
      if ( h.desc ) Object.defineProperty( h.owner, h.name, h.desc );
      else if ( h.fn ) h.owner[h.name] = h.fn;
    }
    this.hook_originals = [];
  }

  private static onDrawImage( ctx: CanvasRenderingContext2D, args: unknown[], ms: number ): void
  {
    const c = this.cur;
    const canvas = ctx.canvas as HTMLCanvasElement | OffscreenCanvas;
    if ( canvas instanceof HTMLCanvasElement && canvas.id === "fpsCanvas" ) return;
    c.draw_ms += ms;
    const in_dom = canvas instanceof HTMLCanvasElement && canvas.isConnected;
    if ( !in_dom ) c.off_ops++;
    else c.ops++;
    c.img++;
    const area = this.destArea( ctx, args ) / MPX;
    c.img_mpx += area;
    if ( ctx.shadowBlur > 0 ) c.shadow++;
    if ( ctx.filter && ctx.filter !== "none" ) c.filter++;

    const src = args[0] as object | null;
    let first = false;
    if ( src && typeof src === "object" && !this.seen_sources.has( src ) )
    {
      this.seen_sources.add( src );
      const s = src as { width?: number; height?: number };
      const mpx = ( s.width ?? 0 ) * ( s.height ?? 0 ) / MPX;
      if ( mpx > 0.02 )
      {
        first = true;
        c.new_src++;
        c.new_src_mpx += mpx;
      }
    }
    if ( ms >= SLOW_OP_MS || ( first && ms >= 0.5 ) )
      this.slow( first ? "2d.drawImage(first)" : "2d.drawImage", `${this.sourceLabel( src )} -> ${this.canvasLabel( canvas )} ${area.toFixed( 2 )}Mpx`, ms );
  }

  private static onText( ctx: CanvasRenderingContext2D, stroke: boolean ): void
  {
    const canvas = ctx.canvas as HTMLCanvasElement | OffscreenCanvas;
    if ( canvas instanceof HTMLCanvasElement && canvas.id === "fpsCanvas" ) return;
    if ( stroke ) this.cur.stroke++;
    else this.cur.text++;
    if ( ctx.shadowBlur > 0 ) this.cur.shadow++;
    if ( ctx.filter && ctx.filter !== "none" ) this.cur.filter++;
  }

  private static onShape( ctx: CanvasRenderingContext2D ): void
  {
    if ( ctx.shadowBlur > 0 ) this.cur.shadow++;
    if ( ctx.filter && ctx.filter !== "none" ) this.cur.filter++;
    this.cur.ops++;
  }

  private static slow( kind: string, label: string, ms: number ): void
  {
    if ( this.slow_ops.length < 60 ) this.slow_ops.push( { kind, label, ms } );
    if ( ms >= 8 ) this.event( "slow-op", `${kind} ${label} ${ms.toFixed( 1 )}ms` );
  }

  private static destArea( ctx: CanvasRenderingContext2D, args: unknown[] ): number
  {
    let w = 0, h = 0;
    if ( args.length >= 9 ) { w = args[7] as number; h = args[8] as number; }
    else if ( args.length >= 5 ) { w = args[3] as number; h = args[4] as number; }
    else { const s = args[0] as { width?: number; height?: number }; w = s?.width ?? 0; h = s?.height ?? 0; }
    const m = ctx.getTransform();
    return Math.abs( w * h * ( m.a * m.d - m.b * m.c ) );
  }

  private static texPixels( args: unknown[] ): number
  {
    for ( const a of args )
    {
      if ( a && typeof a === "object" && "width" in ( a as object ) && "height" in ( a as object ) )
      {
        const s = a as { width: number; height: number };
        return s.width * s.height;
      }
    }
    // texImage2D(target, level, internalformat, width, height, ...)
    if ( typeof args[3] === "number" && typeof args[4] === "number" ) return ( args[3] as number ) * ( args[4] as number );
    return 0;
  }

  private static texLabel( args: unknown[], px: number ): string
  {
    for ( const a of args )
      if ( a && typeof a === "object" && "width" in ( a as object ) ) return this.sourceLabel( a as object );
    return `${( px / MPX ).toFixed( 2 )}Mpx`;
  }

  private static sourceLabel( src: object | null ): string
  {
    if ( !src ) return "?";
    const s = src as { width?: number; height?: number };
    const size = `${s.width ?? 0}x${s.height ?? 0}`;
    const known = this.image_labels.get( src );
    if ( known ) return `${known} ${size}`;
    if ( src instanceof HTMLImageElement ) return `${( src.currentSrc || src.src ).split( "/" ).pop()?.split( "?" )[0] ?? "img"} ${size}`;
    if ( typeof HTMLCanvasElement !== "undefined" && src instanceof HTMLCanvasElement ) return `canvas#${src.id || "?"} ${size}`;
    if ( typeof OffscreenCanvas !== "undefined" && src instanceof OffscreenCanvas ) return `offscreen ${size}`;
    if ( typeof ImageBitmap !== "undefined" && src instanceof ImageBitmap )
    {
      const now = performance.now();
      if ( now - this.labels_built_at > 500 )
      {
        this.labels_built_at = now;
        this.buildImageLabels();
        const again = this.image_labels.get( src );
        if ( again ) return `${again} ${size}`;
      }
      return `bitmap ${size}`;
    }
    return `${( src.constructor as { name?: string } )?.name ?? "src"} ${size}`;
  }

  private static buildImageLabels(): void
  {
    const holder = ImagesHolder as unknown as { images_map?: Map<ImgID, object>; descriptors?: { id: ImgID; url: string }[] };
    const urls = new Map<ImgID, string>();
    for ( const d of holder.descriptors ?? [] ) urls.set( d.id, d.url );
    for ( const [ id, bmp ] of holder.images_map ?? [] )
    {
      if ( this.image_labels.has( bmp ) ) continue;
      const file = urls.get( id )?.split( "/" ).pop() ?? "";
      this.image_labels.set( bmp, `${ImgID[id] ?? id}${file ? `(${file})` : ""}` );
    }
  }

  private static canvasLabel( canvas: HTMLCanvasElement | OffscreenCanvas ): string
  {
    if ( canvas instanceof HTMLCanvasElement ) return `#${canvas.id || ( canvas.isConnected ? "?" : "detached" )}`;
    return "offscreen";
  }

  // ---- browser observers ----

  private static readonly INPUT_EVENTS = new Set( [ "pointerdown", "pointerup", "touchstart", "touchend", "click", "keydown" ] );

  private static observe(): void
  {
    const supported = ( PerformanceObserver as unknown as { supportedEntryTypes?: readonly string[] } ).supportedEntryTypes ?? [];
    this.loaf_supported = supported.includes( "long-animation-frame" );
    this.event_timing_supported = supported.includes( "event" );

    const add = ( init: PerformanceObserverInit, fn: ( list: PerformanceObserverEntryList ) => void ): void =>
    {
      try
      {
        const o = new PerformanceObserver( fn );
        o.observe( init );
        this.observers.push( o );
      }
      catch {}
    };

    if ( this.loaf_supported )
      add( { type: "long-animation-frame", buffered: false }, list =>
      {
        for ( const raw of list.getEntries() )
        {
          if ( this.loafs.length >= 5000 ) this.loafs.shift();
          const e = raw as PerformanceEntry & {
            renderStart?: number; styleAndLayoutStart?: number; blockingDuration?: number;
            scripts?: { invoker?: string; sourceFunctionName?: string; sourceURL?: string; sourceCharPosition?: number; duration: number; forcedStyleAndLayoutDuration?: number }[];
          };
          const end = e.startTime + e.duration;
          this.loafs.push( {
            start: e.startTime - this.started_at,
            end: end - this.started_at,
            ms: e.duration,
            render_ms: ( e.renderStart ?? 0 ) > 0 ? end - ( e.renderStart as number ) : 0,
            layout_ms: ( e.styleAndLayoutStart ?? 0 ) > 0 ? end - ( e.styleAndLayoutStart as number ) : 0,
            blocking_ms: e.blockingDuration ?? 0,
            scripts: ( e.scripts ?? [] ).map( s => ( {
              invoker: s.invoker ?? "",
              fn: s.sourceFunctionName ?? "",
              src: `${( s.sourceURL ?? "" ).split( "/" ).pop()?.split( "?" )[0] ?? ""}${s.sourceCharPosition && s.sourceCharPosition > 0 ? `:${s.sourceCharPosition}` : ""}`,
              ms: s.duration,
              layout_ms: s.forcedStyleAndLayoutDuration ?? 0,
            } ) ),
          } );
        }
      } );
    else if ( supported.includes( "longtask" ) )
      add( { type: "longtask", buffered: false }, list =>
      {
        for ( const e of list.getEntries() )
          this.loafs.push( { start: e.startTime - this.started_at, end: e.startTime + e.duration - this.started_at, ms: e.duration, render_ms: 0, layout_ms: 0, blocking_ms: 0, scripts: [] } );
      } );

    if ( this.event_timing_supported )
      add( { type: "event", buffered: false, durationThreshold: 16 } as PerformanceObserverInit, list =>
      {
        for ( const raw of list.getEntries() )
        {
          const e = raw as PerformanceEntry & { processingStart: number; processingEnd: number };
          // One tap fires ~10 pointer/touch events with the same timing; keep the ones that carry handlers.
          if ( !PerfSession.INPUT_EVENTS.has( e.name ) ) continue;
          if ( this.inputs.length >= 2000 ) this.inputs.shift();
          const timing: InputTiming = {
            t: e.startTime - this.started_at,
            name: e.name,
            delay: e.processingStart - e.startTime,
            proc: e.processingEnd - e.processingStart,
            total: e.duration,
          };
          this.inputs.push( timing );
          if ( timing.total >= 100 )
            this.event( "slow-input", `${e.name} total ${timing.total.toFixed( 0 )}ms (delay ${timing.delay.toFixed( 0 )} handlers ${timing.proc.toFixed( 0 )} to-paint ${( timing.total - timing.delay - timing.proc ).toFixed( 0 )})` );
        }
      } );

    add( { type: "resource", buffered: false }, list =>
    {
      for ( const raw of list.getEntries() )
      {
        const e = raw as PerformanceResourceTiming;
        if ( this.resources.length >= 1500 ) continue;
        const name = e.name.replace( /^https?:\/\/[^/]+/, "" ).split( "?" )[0];
        const kb = ( e.transferSize || e.encodedBodySize || 0 ) / 1024;
        this.resources.push( { t: e.startTime - this.started_at, name, ms: e.duration, kb } );
        if ( e.duration >= 150 ) this.event( "resource", `${name} ${e.duration.toFixed( 0 )}ms ${kb.toFixed( 0 )}KB` );
      }
    } );
  }

  private static listenDom(): void
  {
    const on = ( target: EventTarget, type: string, fn: EventListener, capture = true ): void =>
    {
      target.addEventListener( type, fn, { capture, passive: true } );
      this.dom_listeners.push( { target, type, fn, capture } );
    };
    on( window, "pointerdown", ( ev: Event ) =>
    {
      const p = ev as PointerEvent;
      if ( this.isOwnUi( p.target ) ) return;
      const text = `(${Math.round( p.clientX )},${Math.round( p.clientY )}) on ${this.contextText()}`;
      this.last_tap = { t: performance.now(), text };
      this.event( "tap", text );
    } );
    on( document, "visibilitychange", () =>
    {
      if ( document.visibilityState === "hidden" )
      {
        this.hidden_since = performance.now();
        this.event( "app", "hidden" );
        try { localStorage.setItem( REPORT_KEY, this.buildReport() ); }
        catch {}
      }
      else
      {
        this.event( "app", `visible after ${( performance.now() - this.hidden_since ).toFixed( 0 )}ms` );
        this.skip_next = true;
        this.prev_frame_start = 0;
      }
    } );
    if ( document.fonts )
      on( document.fonts as unknown as EventTarget, "loadingdone", () => this.event( "font", "loadingdone" ), false );
  }

  private static unlistenDom(): void
  {
    for ( const l of this.dom_listeners ) l.target.removeEventListener( l.type, l.fn, { capture: l.capture } );
    this.dom_listeners = [];
  }

  private static teardown(): void
  {
    this.recording = false;
    this.has_data = true;
    FpsOverlay.session_marks = false;
    CrashBreadcrumbs.listener = null;
    for ( const o of this.observers ) o.disconnect();
    this.observers = [];
    this.unlistenDom();
    this.removeHooks();
    this.unwrapAll();
    this.hideHud();
  }

  // ---- context ----

  private static screenName(): string
  {
    return String( ScreenManager.getCurrentScreen() ?? "none" );
  }

  private static dialogStack(): object[]
  {
    return ( ( DialogManager as unknown as { dialog_stack?: object[] } ).dialog_stack ?? [] ).slice();
  }

  private static overlayStack(): object[]
  {
    return ( ( OverlayManager as unknown as { overlay_stack?: object[] } ).overlay_stack ?? [] ).slice();
  }

  private static nameOf( o: object ): string
  {
    const el = o as { name?: () => string; constructor?: { name?: string } };
    try { return ( typeof el.name === "function" ? el.name() : "" ) || el.constructor?.name || "?"; }
    catch { return el.constructor?.name || "?"; }
  }

  private static refreshStackNames(): void
  {
    this.dialog_names = this.dialogStack().map( d => this.nameOf( d ) ).join( ">" );
    this.overlay_names = this.overlayStack().map( o => this.nameOf( o ) ).join( ">" );
  }

  private static contextText(): string
  {
    this.refreshStackNames();
    return `${this.screenName()}${this.dialog_names ? ` dialog=${this.dialog_names}` : ""}${this.overlay_names ? ` overlay=${this.overlay_names}` : ""}`;
  }

  private static heapMb(): number
  {
    const memory = ( performance as unknown as { memory?: { usedJSHeapSize: number } } ).memory;
    return memory ? memory.usedJSHeapSize / 1048576 : 0;
  }

  private static memText(): string
  {
    const m = MemoryProbe.forSnapshot();
    if ( !m ) return "";
    return Object.entries( m ).map( ( [ k, v ] ) => `${k}=${v}` ).join( " " );
  }

  private static isOwnUi( target: EventTarget | null ): boolean
  {
    return !!target && target instanceof Node && ( ( this.hud?.contains( target ) ?? false ) || ( this.panel?.contains( target ) ?? false ) );
  }

  // ---- report ----

  private static q( sorted: number[], p: number ): number
  {
    if ( !sorted.length ) return 0;
    return sorted[Math.min( sorted.length - 1, Math.floor( ( sorted.length - 1 ) * p ) )];
  }

  private static f( v: number, d = 1 ): string { return v.toFixed( d ); }

  private static loafFor( t: number, dt: number ): string
  {
    const from = t - dt - 4;
    const hits = this.loafs.filter( l => l.end > from && l.start < t + 4 );
    if ( !hits.length ) return this.loaf_supported ? "none" : "n/a";
    return hits.map( l =>
    {
      const scripts = [ ...l.scripts ].sort( ( a, b ) => b.ms - a.ms ).slice( 0, 4 )
        .map( s => `${s.invoker || "?"}${s.fn ? ` fn=${s.fn}` : ""}${s.src ? ` @${s.src}` : ""} ${this.f( s.ms, 0 )}ms${s.layout_ms > 1 ? ` forced-layout ${this.f( s.layout_ms, 0 )}` : ""}` );
      return `[${this.f( l.ms, 0 )}ms render ${this.f( l.render_ms, 0 )} style/layout ${this.f( l.layout_ms, 0 )} blocking ${this.f( l.blocking_ms, 0 )}${scripts.length ? ` | ${scripts.join( "; " )}` : ""}]`;
    } ).join( " " );
  }

  private static inputFor( t: number, dt: number ): string
  {
    const hits = this.inputs.filter( i => i.t + i.total > t - dt - 4 && i.t < t + 4 );
    return hits.map( i => `${i.name} delay ${this.f( i.delay, 0 )} handlers ${this.f( i.proc, 0 )} total ${this.f( i.total, 0 )}` ).join( "; " );
  }

  private static header(): string[]
  {
    const dpr = Game.getDpr();
    const nav = navigator as Navigator & { deviceMemory?: number };
    const dur = ( performance.now() - this.started_at ) / 1000;
    let quality = "?";
    try { quality = String( GraphicsSettings.get() ); }
    catch {}
    return [
      `PERFSESSION v1  started ${this.started_wall}  duration ${this.f( dur, 0 )}s  branch=${GIT_BRANCH}  build=${typeof __CRASH_BUILD_ID__ === "string" ? __CRASH_BUILD_ID__ : "dev"}`,
      `ua: ${navigator.userAgent}`,
      `device: cores=${navigator.hardwareConcurrency ?? "?"} mem=${nav.deviceMemory ?? "?"}GB screen=${screen.width}x${screen.height} css=${Game.getScreenWidth()}x${Game.getScreenHeight()} `
        + `dpr=${this.f( dpr, 2 )} window.dpr=${this.f( window.devicePixelRatio, 2 )} canvas=${this.f( Game.getScreenWidth() * Game.getScreenHeight() * dpr * dpr / MPX, 2 )}Mpx graphics=${quality}`,
      `vsync≈${this.f( this.vsync )}ms (${this.f( 1000 / this.vsync, 0 )}Hz)  hitch = dt ≥ ${this.f( this.hitchThreshold() )}ms  loaf=${this.loaf_supported ? "yes" : "no"} `
        + `event-timing=${this.event_timing_supported ? "yes" : "no"} heap-api=${PerfSession.heapMb() > 0 ? "yes" : "no"} frames=${this.frame_count} marks=${this.marks}`,
      `mem now: ${this.memText() || "n/a"}`,
    ];
  }

  private static buildReport( full = true ): string
  {
    const L: string[] = this.header();
    const vs = this.vsync;

    // ---- per screen ----
    L.push( "", "== SCREENS (all frames on that screen) ==",
      "screen               frames   fps   p50   p95   p99    max  hitch  >50  >100  jank_s" );
    const pad = ( s: string, n: number ) => s.length >= n ? s.slice( 0, n ) : s + " ".repeat( n - s.length );
    const num = ( v: number, n: number, d = 1 ) => { const t = v.toFixed( d ); return " ".repeat( Math.max( 0, n - t.length ) ) + t; };
    const th = this.hitchThreshold();
    for ( const [ screen, dts ] of [ ...this.screen_dts.entries() ].sort( ( a, b ) => b[1].length - a[1].length ) )
    {
      const sorted = [ ...dts ].sort( ( a, b ) => a - b );
      const total = dts.reduce( ( a, b ) => a + b, 0 );
      const jank = dts.reduce( ( a, b ) => a + Math.max( 0, b - vs ), 0 );
      L.push( `${pad( screen, 20 )} ${num( dts.length, 6, 0 )} ${num( 1000 * dts.length / Math.max( 1, total ), 5 )} ${num( this.q( sorted, 0.5 ), 5 )} ${num( this.q( sorted, 0.95 ), 5 )} `
        + `${num( this.q( sorted, 0.99 ), 5 )} ${num( sorted[sorted.length - 1] ?? 0, 6, 0 )} ${num( dts.filter( d => d >= th ).length, 6, 0 )} ${num( dts.filter( d => d >= 50 ).length, 4, 0 )} `
        + `${num( dts.filter( d => d >= 100 ).length, 5, 0 )} ${num( jank / 1000, 7, 2 )}` );
    }
    L.push( "  jank_s = sum over frames of (dt - vsync): time the player saw a frozen picture" );

    // ---- causes ----
    L.push( "", `== HITCH CAUSES (${this.hitch_total} hitches, heuristic) ==` );
    for ( const [ cause, v ] of [ ...this.hitch_by_cause.entries() ].sort( ( a, b ) => b[1].ms - a[1].ms ) )
      L.push( `  ${num( v.n, 5, 0 )}x  lost ${num( v.ms, 7, 0 )}ms  ${cause}` );

    // ---- transitions ----
    const trs = this.transitions.filter( t => t.frames > 0 );
    if ( trs.length )
    {
      L.push( "", "== TRANSITIONS (first 1.5 s after a screen/dialog change) ==",
        "   t_s  kind          name                          1st_dt  max_dt hitch  jank  settle  trigger" );
      for ( const t of trs )
        L.push( `${num( t.t / 1000, 6, 2 )}  ${pad( t.kind, 13 )} ${pad( t.name, 29 )} ${num( t.first_dt, 6, 0 )} ${num( t.max_dt, 7, 0 )} ${num( t.hitches, 5, 0 )} ${num( t.jank_ms, 5, 0 )} `
          + `${t.settle_ms < 0 ? "  >1500" : num( t.settle_ms, 7, 0 )}  ${t.trigger}` );
      L.push( "  settle = ms until 10 frames in a row hit vsync; jank = sum(dt - vsync) in the window" );

      const worst = new Map<string, { n: number; max: number; jank: number }>();
      for ( const t of trs )
      {
        const key = `${t.kind} ${t.name}`;
        const w = worst.get( key ) ?? { n: 0, max: 0, jank: 0 };
        w.n++;
        w.max = Math.max( w.max, t.max_dt );
        w.jank += t.jank_ms;
        worst.set( key, w );
      }
      L.push( "", "== TRANSITIONS BY TARGET (avg jank per open) ==" );
      for ( const [ key, w ] of [ ...worst.entries() ].sort( ( a, b ) => b[1].jank / b[1].n - a[1].jank / a[1].n ).slice( 0, 30 ) )
        L.push( `  ${pad( key, 44 )} x${w.n}  avg_jank ${num( w.jank / w.n, 5, 0 )}ms  worst_frame ${num( w.max, 4, 0 )}ms` );
    }

    // ---- spans ----
    const spans = [ ...this.session_spans.entries() ];
    if ( spans.length )
    {
      L.push( "", "== METHODS BY SELF TIME INSIDE HITCH FRAMES (top 40) ==",
        "  hitch_self  hitch_calls   all_self   all_calls  max_call  method" );
      for ( const [ key, s ] of spans.filter( s => s[1].hitch_self > 0.5 ).sort( ( a, b ) => b[1].hitch_self - a[1].hitch_self ).slice( 0, 40 ) )
        L.push( `  ${num( s.hitch_self, 10, 1 )} ${num( s.hitch_calls, 12, 0 )} ${num( s.self, 10, 1 )} ${num( s.calls, 11, 0 )} ${num( s.max, 9, 1 )}  ${key}` );
      L.push( "", "== METHODS BY SELF TIME OVERALL (top 30) ==" );
      for ( const [ key, s ] of spans.sort( ( a, b ) => b[1].self - a[1].self ).slice( 0, 30 ) )
        L.push( `  ${num( s.self, 10, 1 )}ms ${num( s.calls, 9, 0 )} calls  avg ${num( s.self / Math.max( 1, s.calls ), 6, 3 )}  max ${num( s.max, 6, 1 )}  ${key}` );
      if ( this.hot_skipped.length )
        L.push( `  unwrapped as hot+cheap (their time folds into the caller): ${this.hot_skipped.slice( 0, 40 ).join( ", " )}` );
      L.push( "  # = prototype method, . = static; self = own time without wrapped callees; async methods count only their sync part" );
    }

    // ---- async ----
    const asyncs = [ ...this.async_stats.entries() ].filter( a => a[1].max >= 30 ).sort( ( a, b ) => b[1].max - a[1].max ).slice( 0, 25 );
    if ( asyncs.length )
    {
      L.push( "", "== ASYNC METHODS (wall time incl. awaits: network, decode, waits; max >= 30ms, top 25) ==" );
      for ( const [ key, a ] of asyncs )
        L.push( `  ${num( a.n, 5, 0 )}x  avg ${num( a.total / a.n, 6, 0 )}ms  max ${num( a.max, 6, 0 )}ms${a.rejected ? `  rejected ${a.rejected}` : ""}  ${key}` );
    }

    // ---- loaf scripts ----
    if ( this.loafs.length )
    {
      const agg = new Map<string, { ms: number; n: number; layout: number }>();
      for ( const l of this.loafs )
        for ( const s of l.scripts )
        {
          const key = `${s.invoker || "?"}${s.fn ? ` fn=${s.fn}` : ""}${s.src ? ` @${s.src}` : ""}`;
          const row = agg.get( key ) ?? { ms: 0, n: 0, layout: 0 };
          row.ms += s.ms; row.n++; row.layout += s.layout_ms;
          agg.set( key, row );
        }
      L.push( "", `== LONG ANIMATION FRAMES: ${this.loafs.length}, scripts by total ms (top 25) ==` );
      for ( const [ key, v ] of [ ...agg.entries() ].sort( ( a, b ) => b[1].ms - a[1].ms ).slice( 0, 25 ) )
        L.push( `  ${num( v.ms, 7, 0 )}ms ${num( v.n, 5, 0 )}x${v.layout > 1 ? ` forced-layout ${this.f( v.layout, 0 )}ms` : ""}  ${key.slice( 0, 140 )}` );
    }

    // ---- inputs ----
    const slow_inputs = this.inputs.filter( i => i.total >= 50 ).sort( ( a, b ) => b.total - a.total ).slice( 0, 25 );
    if ( slow_inputs.length )
    {
      L.push( "", "== SLOW INPUTS (Event Timing, input → next paint, top 25) ==", "   t_s  event          total  delay  handlers  to-paint" );
      for ( const i of slow_inputs )
        L.push( `${num( i.t / 1000, 6, 2 )}  ${pad( i.name, 13 )} ${num( i.total, 6, 0 )} ${num( i.delay, 6, 0 )} ${num( i.proc, 9, 0 )} ${num( i.total - i.delay - i.proc, 9, 0 )}` );
    }

    // ---- hitches ----
    const kept = full ? this.hitches : [ ...this.hitches ].sort( ( a, b ) => b.dt - a.dt ).slice( 0, SHORT_HITCHES );
    const hitches = [ ...kept ].sort( ( a, b ) => a.t - b.t );
    L.push( "", `== HITCHES (${hitches.length} detailed of ${this.hitch_total}${full ? `; beyond ${MAX_DETAILED_HITCHES} only the worst are kept` : ", worst only (short report)"}) ==`,
      "  dt = gap between two loop starts, t = when the late frame started (s from session start).",
      "  loop = JS of the frame that opened the gap [phys game ui2d gl]; between = wrapped JS after it (timers, input, promises);",
      "  out = dt - loop - between (raster/GPU/compositor/GC/unwrapped work). Methods/2d/gl cover the whole gap. Times in ms." );
    for ( const h of hitches )
    {
      const out = h.dt - h.js - h.gap_js - h.probe;
      L.push( "",
        `#${this.f( h.t / 1000, 2 )}s dt=${this.f( h.dt, 0 )} (${this.f( h.dt / vs - 1, 1 )} frames lost)  ${h.screen}${h.dialog ? ` dialog=${h.dialog}` : ""}${h.overlay ? ` overlay=${h.overlay}` : ""}`,
        `  cause: ${h.cause}`,
        `  loop ${this.f( h.js )} [phys ${this.f( h.phys )} game ${this.f( h.game )} ui2d ${this.f( h.ui )} gl ${this.f( h.gl )}]  between ${this.f( h.gap_js )}  out ${this.f( out )}${h.probe >= 0.5 ? `  (probe overhead ${this.f( h.probe )})` : ""}`,
        `  frames before: ${h.prev.join( " " ) || "-"} | after: ${h.next.join( " " ) || "-"}`,
      );
      if ( h.spans.length )
        L.push( `  methods (self/total ms xcalls): ${h.spans.map( s => `${s.key} ${this.f( s.self )}/${this.f( s.total )}${s.calls > 1 ? ` x${s.calls}` : ""}` ).join( "; " )}` );
      const c = h.c;
      L.push( `  2d: img ${c.img} (${this.f( c.img_mpx, 2 )}Mpx, drawImage ${this.f( c.draw_ms )}ms) text ${c.text}+${c.stroke}stroke shape ${c.ops} shadow ${c.shadow} filter ${c.filter} offscreen ${c.off_ops}`
        + `${c.new_src ? ` first-draw ${c.new_src} (${this.f( c.new_src_mpx, 2 )}Mpx)` : ""}${c.resize ? ` canvas-resize ${c.resize} (${this.f( c.resize_ms )}ms)` : ""}` );
      if ( c.tex || c.shader || c.gl_draws || c.readback )
        L.push( `  gl: draws ${c.gl_draws} tex-upload ${c.tex} (${this.f( c.tex_mpx, 2 )}Mpx ${this.f( c.tex_ms )}ms) shader ${c.shader} (${this.f( c.shader_ms )}ms) readback ${c.readback} (${this.f( c.readback_ms )}ms)` );
      if ( h.slow.length )
        L.push( `  slow ops: ${h.slow.map( s => `${s.kind} ${s.label} ${this.f( s.ms )}ms` ).join( "; " )}` );
      L.push( `  heap ${h.heap > 0 ? `${this.f( h.heap )}MB` : "n/a"}${h.gc > 0.05 ? ` gc -${this.f( h.gc )}MB` : ""}` );
      const loaf = this.loafFor( h.t, h.dt );
      if ( loaf !== "n/a" ) L.push( `  loaf: ${loaf}` );
      const input = this.inputFor( h.t, h.dt );
      if ( input ) L.push( `  input: ${input}` );
      if ( h.events.length ) L.push( `  before it: ${h.events.join( " | " )}` );
    }

    // ---- resources ----
    const slow_res = this.resources.filter( r => r.ms >= 100 || r.kb >= 200 ).sort( ( a, b ) => b.ms - a.ms ).slice( 0, 30 );
    if ( slow_res.length )
    {
      L.push( "", `== NETWORK/RESOURCES (${this.resources.length} total; slow or big, top 30) ==` );
      for ( const r of slow_res )
        L.push( `${num( r.t / 1000, 7, 2 )}s ${num( r.ms, 6, 0 )}ms ${num( r.kb, 6, 0 )}KB  ${r.name.slice( 0, 120 )}` );
    }

    // ---- timeline ----
    const timeline = full ? this.timeline : this.timeline.filter( e => e.kind !== "http" && e.kind !== "mem" && e.kind !== "console" ).slice( -SHORT_TIMELINE );
    L.push( "", `== TIMELINE (${timeline.length} of ${this.timeline.length} events${full ? "" : ", short: no http/mem/console, last only"}) ==` );
    for ( const e of timeline )
      L.push( `${num( e.t / 1000, 8, 3 )}  ${pad( e.kind, 10 )} ${e.text}` );

    return L.join( "\n" );
  }

  // ---- output / UI ----

  private static publish( report: string ): void
  {
    this.last_report = report;
    try { localStorage.setItem( REPORT_KEY, report ); }
    catch {}
    // Chunked so logcat / the remote console don't truncate it.
    const CHUNK = 3500;
    for ( let i = 0, part = 1; i < report.length; i += CHUNK, part++ )
      console.log( `[PERFSESSION ${part}/${Math.ceil( report.length / CHUNK )}]\n${report.slice( i, i + CHUNK )}` );
  }

  private static loadSaved(): string
  {
    try { return localStorage.getItem( REPORT_KEY ) ?? ""; }
    catch { return ""; }
  }

  private static stopTouches( el: HTMLElement ): void
  {
    for ( const type of [ "touchstart", "touchend", "touchmove", "pointerdown", "pointerup", "pointermove", "mousedown", "mouseup", "click" ] )
      el.addEventListener( type, ev => ev.stopPropagation() );
  }

  private static button( label: string, action: () => void ): HTMLButtonElement
  {
    const b = document.createElement( "button" );
    b.textContent = label;
    b.style.cssText = "font:bold 11px monospace;padding:5px 7px;background:#312;color:#fff;border:1px solid #f64;border-radius:4px;pointer-events:auto;";
    b.addEventListener( "click", ev => { ev.stopPropagation(); action(); } );
    return b;
  }

  private static showHud(): void
  {
    if ( this.hud ) return;
    const box = document.createElement( "div" );
    box.style.cssText = "position:fixed;left:4px;top:calc(env(safe-area-inset-top, 0px) + 4px);z-index:100001;display:flex;gap:4px;align-items:center;"
      + "background:rgba(0,0,0,0.7);color:#fdd;font:10px/1.2 monospace;padding:3px 4px;border-radius:5px;pointer-events:none;";
    const text = document.createElement( "span" );
    text.textContent = "● REC";
    box.appendChild( text );
    box.appendChild( this.button( "MARK", () => this.mark() ) );
    box.appendChild( this.button( "STOP", () => this.stop() ) );
    this.stopTouches( box );
    document.body.appendChild( box );
    this.hud = box;
    this.hud_text = text;
  }

  private static updateHud(): void
  {
    if ( !this.hud_text ) return;
    const s = Math.floor( ( performance.now() - this.started_at ) / 1000 );
    this.hud_text.textContent = `● ${Math.floor( s / 60 )}:${String( s % 60 ).padStart( 2, "0" )} hitch ${this.hitch_total} worst ${this.worst_dt.toFixed( 0 )}`;
  }

  private static flashHud( text: string ): void
  {
    if ( this.hud_text ) this.hud_text.textContent = text;
    this.last_hud_update = performance.now() + 700;
  }

  private static hideHud(): void
  {
    this.hud?.remove();
    this.hud = null;
    this.hud_text = null;
  }

  public static showPanel( report = this.report(), full_length = report.length ): void
  {
    this.panel?.remove();
    const box = document.createElement( "div" );
    box.style.cssText = "position:fixed;left:4px;right:4px;bottom:4px;top:20vh;z-index:100002;background:rgba(0,0,0,0.9);color:#dfe;"
      + "font:10px/1.25 monospace;border:1px solid #f64;border-radius:6px;display:flex;flex-direction:column;";
    const bar = document.createElement( "div" );
    bar.style.cssText = "display:flex;gap:4px;padding:4px;flex-wrap:wrap;align-items:center;";
    const status = document.createElement( "span" );
    status.textContent = `short ${( report.length / 1024 ).toFixed( 0 )} KB / full ${( full_length / 1024 ).toFixed( 0 )} KB`;
    const copy = ( full: boolean ) => void this.copy( full ).then( ok =>
    {
      status.textContent = ok ? `copied ${full ? "full" : "short"} ✓` : "copy failed — see console [PERFSESSION]";
    } );
    bar.appendChild( this.button( "SAVE TXT", () =>
    {
      status.textContent = "saving…";
      void this.save( true ).then( ok => { status.textContent = ok ? "saved ✓" : "save failed — try COPY"; } );
    } ) );
    bar.appendChild( this.button( "COPY SHORT", () => copy( false ) ) );
    bar.appendChild( this.button( "COPY FULL", () => copy( true ) ) );
    bar.appendChild( this.button( "NEW SESSION", () => { box.remove(); this.panel = null; this.start(); } ) );
    bar.appendChild( this.button( "CLOSE", () => { box.remove(); this.panel = null; } ) );
    bar.appendChild( status );
    const pre = document.createElement( "pre" );
    pre.style.cssText = "margin:0;padding:4px 6px;overflow:auto;white-space:pre;flex:1;user-select:text;-webkit-user-select:text;";
    pre.textContent = report || "no session recorded yet";
    box.appendChild( bar );
    box.appendChild( pre );
    this.stopTouches( box );
    document.body.appendChild( box );
    this.panel = box;
  }
}
